import {
  VideoAssets as RawVideoAsset,
  VideoLibrary,
  hlsTranscodeVideoAssets,
  listVideoAssets,
  list as listVideoLibraryAssets,
  retrieveVideoAssets,
  CreationTypeEnum,
  ListAPIQueryParams,
  UserBasicInformation,
  listGetEventRecordings,
  getEventRoomRecordings,
  Recording,
  deleteVideoLibraryAsset,
  partialUpdateVideoLibraryAsset,
  Source09fEnum
} from '@goldcast/api/backend';
import {
  assetsClipGetClipByVideoLibraryIdRetrieve,
  assetsContentUploadGetContentUploadByVideoLibraryIdRetrieve
} from '@goldcast/api/content';
import logger from '@goldcast/logger';
import JSZip from 'jszip';
import moment from 'moment';

import { transformEditUrlForClipAsset, transformEditUrlForContentUpload } from './utils';

import { UPLOAD_PREFIX } from '@/constants';
import router from '@/router';
import store from '@/store';
import { isEURegion } from '@/utils';
import { isCLEUProcessingEnabled } from '@/utils/clFeatureGating';
import { getEnv } from '@/utils/env';

export const VIDEO_ASSET_TRANSCODING_STATUS = {
  SUBMITTED: 'SUBMITTED',
  PROGRESSING: 'PROGRESSING',
  PROCESSING_HD: 'PROCESSING_HD',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
  READY_TO_PUBLISH: 'READY_TO_PUBLISH'
};

export const VIDEO_TRANSCODING_CODES_DESCRIPTION = {
  TOO_MANY_VIDEO_STREAMS: 'Multiple video streams were detected. Please check if the video is playing properly.',
  TOO_MANY_AUDIO_STREAMS: 'Multiple audio streams were detected. Please check if the video is playing properly.',
  MISSING_VIDEO_STREAM: "This video can't be played. No video stream detected.",
  GENERIC_ERROR: "This video can't be played. We could not process your video.",
  ASPECT_RATIO_MISMATCH: 'Aspect ratio mismatch detected.',
  NON_STANDARD_ASPECT_RATIO: "The video's aspect ratio does not conform to the standard aspect ratio (16:9 or 16:10).",
  RESOLUTION_TOO_LOW: 'Low video resolution.',
  MISSING_AUDIO_STREAM: 'No audio stream detected.',
  POSSIBLE_CORRUPTION_WARNING:
    'Possible corruption detected in video: open your Event Space and watch your full video to confirm it plays as expected.',
  NON_ZERO_VIDEO_START_PTS: 'Non-zero starting timestamp. Video stream playback may lag.',
  NON_ZERO_AUDIO_START_PTS: 'Non-zero starting timestamp. Audio stream playback may lag.',
  UNSUPPORTED_VIDEO_CONTAINER: 'Use an MP4 file for best playback quality.',
  UNSUPPORTED_VIDEO_CODEC: 'This video may have issues playing.  Use H.264 or H.265 encoding.',
  UNSUPPORTED_AUDIO_CODEC: 'Audio in this video may have issues playing. Use AAC or AC3 encoding.',
  AUDIO_FILE_UPLOADED: 'Audio files cannot be played directly in events. You can use .mp4 format for event playback.',
  MULTI_LANGUAGE_SUPPORT_RESTRICTED: 'Non-English video transcription and editing is not available for your account',
  GENERIC_WARNING: '',
  UPLOAD_MULTI_LANGUAGE_SUPPORT_RESTRICTED:
    'Non-English video transcription and editing is not available for your account',
  UPLOAD_LANGUAGE_NOT_SUPPORTED: 'Video transcription and editing is not available for this language'
};

export const EDIT_VIDEO_ERROR_CODES_DESCRIPTION = {
  UNKNOWN_ERROR:
    'Editing is not available for some videos: replaced videos for on-demand or event recordings generated prior April 2023',
  LANGUAGE_NOT_SUPPORTED: 'Non english language videos cannot be edited in Content Lab currently',
  RECORDING_NOT_AVAILABLE: 'No recording found for the event.',
  ASSET_NOT_FOUND: 'This video cannot be edited because it does not exist in Content Lab',
  INVALID_AUDIO: 'This video cannot be edited because it has an invalid audio stream.',
  LEGACY_VIDEO: 'This video cannot be edited because it is a legacy video. Please reupload it in Content Lab.',
  TRANSCRIPT_NOT_AVAILABLE: 'This video cannot be edited because it does not have a transcript.',
  SERVER_ERROR: 'Something went wrong. If the problem persists, please contact support.',
  MULTI_LANGUAGE_SUPPORT_RESTRICTED: 'Non-English video transcription and editing is not available for your account'
};

export type VideoAsset = {
  id: string;
  name: string;
  thumbnail: string;
  createdAt: string;
  updatedAt: string;
  source: Source09fEnum;
  createdBy: string;
  selectable: boolean;
  urls: {
    hls: string;
    mp4: string;
    thumbnail: string;
  };
  warnings: string[];
  errors: string[];
  status: keyof typeof VIDEO_ASSET_TRANSCODING_STATUS;
  duration: string;
  creationType: VideoLibrary['creation_type'];
  usageCount: number;
  workspace: VideoLibrary['workspace'];
  // Marks this asset as a duplicate of another clip. Mirrors the `duplicated_from` check used in
  // Content Studio (see `getIsDuplicateRecording`), surfaced by the backend as `is_duplicate`.
  isDuplicate: boolean;
};

export type VideoLibrarySortOrder = 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at';

export type VideoAssetEditConfig = {
  url: string | null;
  isLoading: boolean;
  errorCode?: string;
  contentId?: string;
  projectId?: string;
};

export const VALID_VL_SOURCES = ['UPLOADED'] satisfies ListAPIQueryParams['source'];

export const editConfigByVideoId: Record<string, VideoAssetEditConfig> = {};

export function getValidVLSource(): ListAPIQueryParams['source'] {
  const VL_SOURCE: ListAPIQueryParams['source'] = [];

  if (router.currentRoute.value.path !== '/content') {
    // Hide Legacy Manual Uploads when opening VL in Content Lab
    VL_SOURCE.push('UPLOADED');
  }

  if (store?.getters['org/getCurrentOrg']?.Use_CL_Uploads_In_VL) {
    VL_SOURCE.push('CL_UPLOAD');
  }

  if (!isEURegion()) {
    VL_SOURCE.push('EVENT_RECORDING');
  }

  if (store?.getters['org/getCurrentOrg']?.isRecordingStudioPricingEnabled) {
    VL_SOURCE.push('DIRECT_RECORDING');
  }

  if (store?.getters['org/getCurrentOrg']?.showVideoLibraryClipPublish) {
    VL_SOURCE.push('CLIP');
  }

  return VL_SOURCE;
}

function getVideoDurationFromSeconds(duration: number) {
  if (isNaN(Number(duration)) || !duration) {
    return '-';
  }
  return moment.utc(duration * 1000).format('HH:mm:ss');
}

function transformTranscodeStatusCodes(warningCodes: string[]): string[] {
  return warningCodes.map(code => {
    return VIDEO_TRANSCODING_CODES_DESCRIPTION[code] || code;
  });
}

function transformTranscodeStatus(rawVideoAsset: RawVideoAsset | VideoLibrary) {
  if (
    rawVideoAsset.transcode_status === 'PROGRESSING' &&
    rawVideoAsset.source === 'CL_UPLOAD' &&
    rawVideoAsset.client_hls_path
  ) {
    return 'PROCESSING_HD';
  }

  if (rawVideoAsset.transcode_status === 'PROGRESSING' && rawVideoAsset.source === 'UPLOAD') {
    return 'PROCESSING_HD';
  }

  return rawVideoAsset.transcode_status || 'PROGRESSING';
}

function getFormattedDateTime(date: string | null | undefined) {
  if (!date) {
    return '';
  }

  return moment(date).tz(moment.tz.guess()).format('MMM D, YYYY, h:mm A z');
}

function transformCreatedBy(createdBy: UserBasicInformation | null, creationType?: CreationTypeEnum): string {
  if (creationType === 'RECORDED' && !createdBy) {
    return 'Goldcast Recorder';
  }

  if (!createdBy) {
    return '';
  }

  return `${createdBy.first_name} ${createdBy.last_name}`;
}

export const isVideoAssetSelectable = (rawVideoAsset: RawVideoAsset | VideoLibrary) => {
  if (rawVideoAsset.source === 'UPLOAD') {
    return true;
  }

  if (
    rawVideoAsset.source === 'CL_UPLOAD' &&
    (rawVideoAsset.transcode_status === 'ERROR' || rawVideoAsset.transcoder_warnings?.includes('AUDIO_FILE_UPLOADED'))
  ) {
    return false;
  }

  return true;
};

export function transformRawVideoAsset(rawVideoAsset: RawVideoAsset | VideoLibrary): VideoAsset {
  const cloudfrontBaseUrl = getEnv('VUE_APP_CLOUDFRONT_URL');
  const env = UPLOAD_PREFIX;
  const orgId = rawVideoAsset?.organization;
  const path = rawVideoAsset.path ?? '';
  const isProcessedByContentLab = rawVideoAsset.source !== 'UPLOAD';
  const isVirtualAsset = rawVideoAsset.creation_type === 'VIRTUAL';

  return {
    id: rawVideoAsset.id || '',
    name: rawVideoAsset.name || '',
    thumbnail: rawVideoAsset.thumbnail || '',
    createdAt: getFormattedDateTime(rawVideoAsset.created_at),
    updatedAt: getFormattedDateTime(rawVideoAsset.updated_at),
    source: rawVideoAsset.source as Source09fEnum,
    createdBy: transformCreatedBy(rawVideoAsset?.created_by, rawVideoAsset.creation_type),
    selectable: isVideoAssetSelectable(rawVideoAsset),
    urls:
      isProcessedByContentLab || isVirtualAsset
        ? {
            hls: rawVideoAsset?.client_hls_path || rawVideoAsset?.hls_path || rawVideoAsset?.urls?.hls || '',
            mp4: rawVideoAsset.path
              ? rawVideoAsset.path.includes('.mp4') || rawVideoAsset.path.includes('.mp3')
                ? `${cloudfrontBaseUrl}/${rawVideoAsset.path}`
                : `${cloudfrontBaseUrl}/${rawVideoAsset.path}_${env}.mp4`
              : '',
            thumbnail:
              ['CLIP', 'CL_UPLOAD'].includes(rawVideoAsset.source as string) && rawVideoAsset?.thumbnail
                ? rawVideoAsset?.thumbnail
                : `${cloudfrontBaseUrl}/${rawVideoAsset.thumbnail}`
          }
        : {
            hls: rawVideoAsset.urls?.hls || '',
            mp4: `${cloudfrontBaseUrl}/${path}_${env}.mp4`,
            thumbnail: `${cloudfrontBaseUrl}/organization/${orgId}/thumbnails/${rawVideoAsset.thumbnail}`
          },
    warnings: transformTranscodeStatusCodes(rawVideoAsset.transcoder_warnings || []),
    errors: transformTranscodeStatusCodes(rawVideoAsset.transcoder_errors || []),
    status: transformTranscodeStatus(rawVideoAsset),
    duration: getVideoDurationFromSeconds(rawVideoAsset.duration || 0),
    creationType: rawVideoAsset.creation_type,
    usageCount: rawVideoAsset.video_usage_count || 0,
    workspace: rawVideoAsset.workspace || '',
    isDuplicate: rawVideoAsset.is_duplicate || false
  };
}

export function transformRoomRecording(recording: Recording, id: number) {
  return {
    ...recording,
    id,
    name: recording.name || '',
    createdAt: getFormattedDateTime(recording.created_at)
  };
}

function startVideoTranscoding(rawVideoAsset: any) {
  return new Promise(resolve => {
    hlsTranscodeVideoAssets({
      id: rawVideoAsset.id,
      body: {}
    })
      .then(() => {
        rawVideoAsset.transcode_status = VIDEO_ASSET_TRANSCODING_STATUS.SUBMITTED;
        store.dispatch(
          'studioToast/triggerToast',
          {
            mode: 'success',
            msg: 'Video is uploaded and is being processed...'
          },
          { root: true }
        );
        resolve(rawVideoAsset);
      })
      .catch(() => {
        rawVideoAsset.transcode_status = VIDEO_ASSET_TRANSCODING_STATUS.ERROR;
        store.dispatch(
          'studioToast/triggerToast',
          {
            mode: 'error',
            msg: 'Unable to start video transcoding...'
          },
          { root: true }
        );
        resolve(rawVideoAsset);
      });
  });
}

/**
 * @deprecated Use getVideoLibraryAssets instead
 */
export function getVideoAssets(params: {
  search?: string;
  offset?: number;
  limit?: number;
  ordering?: VideoLibrarySortOrder;
  workspace?: string;
  workspace__isnull?: boolean;
}): Promise<{ totalCount: number; assets: VideoAsset[] }> {
  return new Promise((resolve, reject) => {
    listVideoAssets({
      queryParams: {
        ...params,
        ordering: (params.ordering || '-created_at') as VideoLibrarySortOrder,
        creation_type: 'UPLOADED',
        workspace: params.workspace,
        workspace__isnull: params.workspace__isnull
      }
    })
      .then(res => {
        resolve({
          totalCount: res?.count || 0,
          assets: res?.results?.map(transformRawVideoAsset) || []
        });
      })
      .catch(reject);
  });
}

export function getVideoLibraryAssets({
  includeDuplicates,
  ...params
}: {
  search?: string;
  offset?: number;
  limit?: number;
  ordering?: VideoLibrarySortOrder;
  workspace?: string;
  source?: ListAPIQueryParams['source'];
  created_by?: ListAPIQueryParams['created_by'];
  event_ids?: ListAPIQueryParams['event_ids'][];
  event_tag?: ListAPIQueryParams['event_tag'];
  series_id?: ListAPIQueryParams['series_id'][];
  includeDuplicates?: boolean;
}): Promise<{ totalCount: number; assets: VideoAsset[] }> {
  return new Promise((resolve, reject) => {
    listVideoLibraryAssets({
      queryParams: {
        ...params,
        ordering: (params.ordering || '-created_at') as VideoLibrarySortOrder,
        workspace: params.workspace,
        source: params.source,
        created_by: params?.created_by,
        event_ids: params?.event_ids as any,
        event_tag: params?.event_tag,
        series_id: params?.series_id as any,
        // `include_duplicates` isn't in the generated ListAPIQueryParams yet; the fetcher serializes
        // raw query params, so we pass it through only when the clip-duplication flag is on so the
        // backend also returns duplicate recordings in the listing.
        ...(includeDuplicates ? { include_duplicates: true } : {})
      } as ListAPIQueryParams,
      queryParamsStringifyOptions: { arrayFormat: 'repeat' }
    })
      .then(res => {
        resolve({
          totalCount: res?.count || 0,
          assets: res?.results?.map(transformRawVideoAsset) || []
        });
      })
      .catch(reject);
  });
}

export function getEventVideoLibraryRecordings(params: {
  event_id: string;
}): Promise<{ totalCount: number; assets: VideoAsset[] }> {
  return new Promise((resolve, reject) => {
    listGetEventRecordings({ queryParams: params })
      .then(res => {
        resolve({
          totalCount: res.length,
          assets: res.map(transformRawVideoAsset) || []
        });
      })
      .catch(reject);
  });
}

export function getRoomRecordings(params: { event_id: string }): Promise<{ assets }> {
  return new Promise((resolve, reject) => {
    getEventRoomRecordings({ id: params.event_id, queryParams: {} })
      .then((response: any) => {
        resolve({
          assets: response.map((res: Recording, i: number) => transformRoomRecording(res, i)) || []
        });
      })
      .catch(reject);
  });
}

export function getVideoAsset(id: string): Promise<VideoAsset> {
  return new Promise((resolve, reject) => {
    retrieveVideoAssets({
      id
    })
      .then(res => resolve(transformRawVideoAsset(res)))
      .catch(reject);
  });
}

export function deleteVideoAsset(id: string): Promise<any> {
  return new Promise((resolve, reject) => {
    deleteVideoLibraryAsset({
      id
    })
      .then(resolve)
      .catch(error => {
        reject(error?.message || 'Something went wrong. Please try again.');
      });
  });
}

export function renameVideoAsset(id: string, name: string): Promise<any> {
  return new Promise((resolve, reject) => {
    partialUpdateVideoLibraryAsset({
      id,
      body: {
        name
      }
    })
      .then(res => resolve(transformRawVideoAsset(res)))
      .catch(reject);
  });
}

export function uploadVideoAsset(details): Promise<VideoAsset> {
  return new Promise((resolve, reject) => {
    store
      .dispatch(
        'makeapicall',
        {
          url: `/core/video_assets/`,
          method: 'POST',
          body: details,
          noContentType: true
        },
        { root: true }
      )
      .then(rawVideoAsset => {
        return startVideoTranscoding(rawVideoAsset);
      })
      .then(rawVideoAsset => {
        resolve(transformRawVideoAsset(rawVideoAsset as RawVideoAsset));
      })
      .catch(reject);
  });
}

export function downloadFileFromUrl(url: string, fileName: string, fileExtension: string): Promise<void> {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then((response: any) => response.blob())
      .then((videoBlob: any) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(videoBlob);
        link.download = fileName + '.' + fileExtension;
        link.click();
        URL.revokeObjectURL(link.href);
        resolve();
      })
      .catch(reject);
  });
}

export function downloadVideoMp4(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadVideoAsset(videoAsset: VideoAsset) {
  if (!videoAsset?.urls?.mp4) {
    store.dispatch('studioToast/triggerToast', { mode: 'error', msg: 'Video URL is not available' });
    return;
  }

  downloadVideoMp4(videoAsset.urls.mp4, videoAsset.name);
}

export const validateEditConfigByVideoId = (videoAsset: VideoAsset) => {
  const currentEditConfig = editConfigByVideoId[videoAsset.id];
  // This ensures that if a user is navigating back and forth while the CL Upload is processing,
  // the edit link is updated to the correct URL when it goes into VOD processing.
  if (
    videoAsset.source === 'CL_UPLOAD' &&
    currentEditConfig.url?.includes('import') &&
    (videoAsset.status === 'PROCESSING_HD' ||
      videoAsset.status === 'COMPLETE' ||
      videoAsset.status === 'READY_TO_PUBLISH')
  ) {
    const urlParts = currentEditConfig.url?.split('/').filter(Boolean) || [];
    const contentId = urlParts[urlParts.length - 1];
    const projectId = urlParts[urlParts.length - 2];
    editConfigByVideoId[videoAsset.id] = {
      url: `/content/${projectId}/${contentId}/recordings`,
      isLoading: false
    };
  }
};

export const setEditConfigByVideoId = async (videoAsset: VideoAsset) => {
  if (videoAsset.source === 'UPLOAD') {
    editConfigByVideoId[videoAsset.id] = { url: null, isLoading: false, errorCode: 'LEGACY_VIDEO' };
    return;
  }

  editConfigByVideoId[videoAsset.id] = { url: null, isLoading: true };

  try {
    let editUrl = '';
    let contentId = '';
    let projectId = '';

    if (videoAsset.source === 'CL_UPLOAD') {
      const contentUpload = await assetsContentUploadGetContentUploadByVideoLibraryIdRetrieve({
        queryParams: { video_library_id: videoAsset.id }
      });
      editUrl = transformEditUrlForContentUpload(videoAsset, contentUpload);
      contentId = contentUpload.id || '';
      projectId = contentUpload.project_id || '';
    } else {
      const clipAsset = await assetsClipGetClipByVideoLibraryIdRetrieve({
        queryParams: { video_asset: videoAsset.id }
      });
      editUrl = transformEditUrlForClipAsset(videoAsset, clipAsset);
      contentId = clipAsset.content?.id || '';
      projectId = clipAsset.content?.project?.id || '';
    }

    if (editUrl) {
      editConfigByVideoId[videoAsset.id] = {
        url: editUrl,
        isLoading: false,
        contentId,
        projectId
      };
    } else {
      editConfigByVideoId[videoAsset.id] = {
        url: null,
        isLoading: false,
        errorCode: 'UNKNOWN_ERROR'
      };
    }
  } catch (error) {
    logger.warn('[videoLibraryService] Video Library: Error fetching clip asset for edit', error);

    try {
      editConfigByVideoId[videoAsset.id] = {
        url: null,
        isLoading: false,
        errorCode: (error as any)?.apiErrorCode || 'UNKNOWN_ERROR'
      };
    } catch (error) {
      logger.error('[videoLibraryService] Video Library: Error parsing error', error);
      editConfigByVideoId[videoAsset.id] = { url: null, isLoading: false, errorCode: 'SERVER_ERROR' };
    }
  }
};

export const getIsVideoAssetEditable = (video: VideoAsset) => {
  if (isEURegion() || !isCLEUProcessingEnabled()) {
    return false;
  }

  if (video.source === 'UPLOAD') {
    return false;
  }

  if (['EVENT_RECORDING', 'DIRECT_RECORDING'].includes(video.source)) {
    return video.status === 'COMPLETE' || video.status === 'READY_TO_PUBLISH';
  }

  if (video.source === 'CL_UPLOAD') {
    return video.status !== 'ERROR';
  }

  if (video.source === 'CLIP') {
    return true;
  }

  return false;
};

export const canPreviewVideoAsset = (videoAsset: VideoAsset) => {
  if (!videoAsset?.urls?.hls) {
    return false;
  }

  // CL Uploads can be previewed while VOD processing is going on
  if (videoAsset.status === 'PROCESSING_HD' && videoAsset.source === 'CL_UPLOAD') {
    return true;
  }

  // All video assets can be previewed when status is COMPLETE
  if (videoAsset.status === 'COMPLETE') {
    return true;
  }

  return false;
};

export const canRenameVideoAsset = (videoAsset: VideoAsset) => {
  if (videoAsset.creationType === 'CLIP' && videoAsset.status === 'PROGRESSING') {
    return false;
  }

  return true;
};

type TranscriptsByFormat = { [format: string]: { [lang: string]: string } };

const getVideoObject = (
  name: string,
  description: string | undefined,
  thumbnailUrl: string,
  uploadDate: string,
  duration: number,
  contentUrl: string,
  chapters: Array<{ title?: string; start_time: number }>
) => {
  const sortedChapters = chapters.length ? [...chapters].sort((a, b) => a.start_time - b.start_time) : [];

  const hasPart = sortedChapters.map((chapter, index, sorted) => ({
    '@type': 'Clip',
    name: chapter.title || `Chapter ${index + 1}`,
    startOffset: chapter.start_time,
    endOffset: sorted[index + 1]?.start_time ?? duration,
    url: `${contentUrl}#t=${chapter.start_time}`
  }));

  const finalDescription =
    description || (hasPart.length ? `${name}. Topics covered: ${hasPart.map(c => c.name).join(', ')}` : name);

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description: finalDescription,
    thumbnailUrl: [thumbnailUrl],
    uploadDate,
    duration: moment.duration(duration, 'seconds').toISOString(),
    contentUrl,
    ...(hasPart.length && { hasPart })
  };
};

const fetchTranscriptsUsingUrls = async (urls: {
  [type: string]: { [lang: string]: string };
}): Promise<TranscriptsByFormat> => {
  const transcripts: TranscriptsByFormat = {};

  await Promise.all(
    Object.entries(urls).map(async ([type, langUrls]) => {
      if (type !== 'vtt' && type !== 'json' && type !== 'docx') {
        return;
      }

      const languages = Object.keys(langUrls);
      if (!languages.length) {
        return;
      }

      const firstLang = languages[0];
      const url = langUrls[firstLang];

      if (!url) {
        return;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) {
          return;
        }

        if (type === 'docx') {
          const arrayBuffer = await response.arrayBuffer();
          const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
          if (!transcripts[type]) {
            transcripts[type] = {};
          }
          transcripts[type][firstLang] = base64;
        } else {
          const text = await response.text();
          if (!transcripts[type]) {
            transcripts[type] = {};
          }
          transcripts[type][firstLang] = text;
        }
      } catch (error) {
        logger.error(`[videoLibraryService] Error fetching transcript for type ${type}:`, error);
      }
    })
  );

  return transcripts;
};

const fetchTranscriptsPerBroadcast = async (transcriptZipUrl: string): Promise<TranscriptsByFormat> => {
  const transcripts: TranscriptsByFormat = {};

  try {
    const response = await fetch(transcriptZipUrl);
    if (response.ok) {
      const blob = await response.blob();

      const zip = await JSZip.loadAsync(blob).catch(() => null);
      if (zip) {
        await Promise.all(
          Object.entries(zip.files).map(async ([filename, file]) => {
            if (file.dir) {
              return;
            }

            const ext = filename.split('.').pop()?.toLowerCase();
            const baseFilename = filename.substring(0, filename.lastIndexOf('.'));

            if (ext === 'vtt' || ext === 'json') {
              const content = await file.async('text');
              if (!transcripts[ext]) {
                transcripts[ext] = {};
              }
              transcripts[ext][baseFilename] = content;
            } else if (ext === 'docx') {
              const arrayBuffer = await file.async('arraybuffer');
              const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
              if (!transcripts[ext]) {
                transcripts[ext] = {};
              }
              transcripts[ext][baseFilename] = base64;
            }
          })
        );
      } else {
        const content = await blob.text();
        if (content.startsWith('WEBVTT')) {
          if (!transcripts.vtt) {
            transcripts.vtt = {};
          }
          transcripts.vtt['en'] = content;
        }
      }
    }
  } catch (error) {
    logger.error('[videoLibraryService] Error fetching transcripts per broadcast:', error);
  }

  return transcripts;
};

const extractLanguageFromFilename = (filename: string): string | null => {
  const match = filename.match(/[-_]([a-z]{2})$/i);
  if (match) {
    return match[1].toLowerCase();
  }
  return null;
};

const downloadTranscriptsAndVideoObject = async (
  videoName: string,
  transcripts: TranscriptsByFormat,
  videoObject: object
): Promise<void> => {
  const zip = new JSZip();
  zip.file('video-object.json', JSON.stringify(videoObject, null, 2));

  const hasTranscripts = Object.keys(transcripts).length > 0;

  if (hasTranscripts) {
    const usedFilenames = new Set<string>();

    Object.entries(transcripts).forEach(([format, langContent]) => {
      Object.entries(langContent).forEach(([baseFilename, content]) => {
        let lang = extractLanguageFromFilename(baseFilename);

        if (!lang) {
          lang = '';
        }

        let filename = lang ? `transcript-${lang}.${format}` : `transcript.${format}`;

        if (usedFilenames.has(filename)) {
          let suffix = 1;
          const baseLang = lang || 'transcript';
          while (usedFilenames.has(`transcript-${baseLang}-${suffix}.${format}`)) {
            suffix++;
          }
          filename = `transcript-${baseLang}-${suffix}.${format}`;
        }
        usedFilenames.add(filename);

        if (format === 'docx') {
          const binaryString = atob(content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          zip.file(filename, bytes);
        } else {
          zip.file(filename, content);
        }
      });
    });
  } else {
    zip.file('transcript.txt', 'Transcript not available for this video.');
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${videoName.replaceAll(/[^a-zA-Z0-9]/g, '_')}_seo_geo_assets.zip`;
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

export const downloadVideoMetaDataForVL = async (videoId: string, videoName: string): Promise<void> => {
  try {
    const rawVideoAsset = await retrieveVideoAssets({ id: videoId });
    const transformedVideoAsset = transformRawVideoAsset(rawVideoAsset);

    const transcriptUrls: { [type: string]: { [lang: string]: string } } = {};
    if ((rawVideoAsset as any).transcript_path) {
      Object.entries((rawVideoAsset as any).transcript_path).forEach(([type, path]) => {
        if (path && typeof path === 'object') {
          transcriptUrls[type] = {};
          Object.entries(path).forEach(([lang, linkObj]: [string, any]) => {
            if (linkObj?.transcript_link) {
              transcriptUrls[type][lang] = linkObj.transcript_link;
            }
          });
        }
      });
    }

    const transcripts = await fetchTranscriptsUsingUrls(transcriptUrls);

    const chapters = (rawVideoAsset as any).metadata?.chapters || [];
    const videoObject = getVideoObject(
      rawVideoAsset.name || '',
      (rawVideoAsset as any).description,
      transformedVideoAsset.urls.thumbnail,
      new Date(rawVideoAsset.created_at || Date.now()).toISOString(),
      rawVideoAsset.duration || 0,
      transformedVideoAsset.urls.hls,
      chapters
    );

    await downloadTranscriptsAndVideoObject(videoName, transcripts, videoObject);
  } catch (error) {
    logger.error('[videoLibraryService] Error downloading SEO/GEO assets:', error);
    store.dispatch('studioToast/triggerToast', { mode: 'error', msg: 'Failed to download assets' });
  }
};

export const downloadVideoMetaDataForEventRecording = async (
  videoId: string,
  videoName: string,
  transcriptZipUrl: string
): Promise<void> => {
  try {
    const rawVideoAsset = await retrieveVideoAssets({ id: videoId });
    const transformedVideoAsset = transformRawVideoAsset(rawVideoAsset);
    const transcripts = await fetchTranscriptsPerBroadcast(transcriptZipUrl);

    const chapters = (rawVideoAsset as any).metadata?.chapters || [];
    const videoObject = getVideoObject(
      rawVideoAsset.name || '',
      (rawVideoAsset as any).description,
      transformedVideoAsset.urls.thumbnail,
      new Date(rawVideoAsset.created_at || Date.now()).toISOString(),
      rawVideoAsset.duration || 0,
      transformedVideoAsset.urls.hls,
      chapters
    );

    await downloadTranscriptsAndVideoObject(videoName, transcripts, videoObject);
  } catch (error) {
    logger.error('[videoLibraryService] Error downloading transcript with metadata:', error);
    store.dispatch('studioToast/triggerToast', { mode: 'error', msg: 'Failed to download assets' });
  }
};
